import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

const PAGE_SIZE = 10;

export const CATEGORIAS_COMPRA = [
  { id: 'inventario', label: 'Inventario (reventa)' },
  { id: 'servicios', label: 'Servicios' },
  { id: 'gasto_fijo', label: 'Gasto fijo' },
  { id: 'suministros', label: 'Suministros' },
  { id: 'otro', label: 'Otro' },
];

export function mapCompra(snap) {
  return { id: snap.id, ...snap.data() };
}

export async function listPurchases({ ruc, pageSize = PAGE_SIZE, cursor = null, search = '', categoria = 'todos' }) {
  if (!ruc) throw new Error('RUC requerido');
  const constraints = [where('ruc', '==', ruc)];
  if (categoria && categoria !== 'todos') {
    constraints.push(where('categoria', '==', categoria));
  }

  if (search) {
    const term = search.trim().toLowerCase();
    const q = query(collection(db, 'compras'), ...constraints, orderBy('createdAt', 'desc'), limit(pageSize * 3));
    const snap = await getDocs(q);
    const items = snap.docs
      .map(mapCompra)
      .filter((c) => {
        const serie = `${c.serie || ''}-${c.correlativo ?? ''}`.toLowerCase();
        const prov = `${c.proveedorRazonSocial || ''} ${c.proveedorRuc || ''}`.toLowerCase();
        return serie.includes(term) || prov.includes(term);
      })
      .slice(0, pageSize);
    return { items, cursor: null, hasMore: false };
  }

  const q = cursor
    ? query(collection(db, 'compras'), ...constraints, orderBy('createdAt', 'desc'), startAfter(cursor), limit(pageSize))
    : query(collection(db, 'compras'), ...constraints, orderBy('createdAt', 'desc'), limit(pageSize));

  const snap = await getDocs(q);
  const last = snap.docs[snap.docs.length - 1] || null;
  return {
    items: snap.docs.map(mapCompra),
    cursor: last,
    hasMore: snap.docs.length === pageSize,
  };
}

export async function getPurchase(id) {
  const snap = await getDoc(doc(db, 'compras', id));
  if (!snap.exists()) throw new Error('Compra no encontrada');
  return mapCompra(snap);
}

/**
 * Mismo payload que RegistrarCompraScreen (cabecera de compra).
 * NO actualiza stock: el ingreso de inventario se hace con aumentarStock.
 */
export async function createPurchase({ ruc, data }) {
  if (!ruc) throw new Error('RUC requerido');
  if (!data?.serie || !data?.correlativo) {
    throw new Error('Serie y correlativo son obligatorios.');
  }
  const total = parseFloat(data.total);
  if (!total || total <= 0) {
    throw new Error('El total debe ser mayor a 0.');
  }

  return addDoc(collection(db, 'compras'), {
    ruc,
    tipoDocumento: data.tipoDocumento || '01',
    serie: String(data.serie).toUpperCase(),
    correlativo: String(data.correlativo),
    fechaEmision: data.fechaEmision || new Date().toISOString().split('T')[0],
    proveedorRuc: data.proveedorRuc || '',
    proveedorRazonSocial: data.proveedorRazonSocial || '',
    subtotal: parseFloat(data.subtotal) || 0,
    igv: parseFloat(data.igv) || 0,
    total,
    moneda: data.moneda || 'PEN',
    categoria: data.categoria || 'inventario',
    descripcion: data.descripcion || '',
    archivoNombre: null,
    archivoUrl: null,
    estado: 'registrado',
    createdAt: serverTimestamp(),
  });
}

export async function updatePurchase(id, data) {
  const payload = {
    tipoDocumento: data.tipoDocumento || '01',
    serie: String(data.serie || '').toUpperCase(),
    correlativo: String(data.correlativo || ''),
    fechaEmision: data.fechaEmision || new Date().toISOString().split('T')[0],
    proveedorRuc: data.proveedorRuc || '',
    proveedorRazonSocial: data.proveedorRazonSocial || '',
    subtotal: parseFloat(data.subtotal) || 0,
    igv: parseFloat(data.igv) || 0,
    total: parseFloat(data.total) || 0,
    moneda: data.moneda || 'PEN',
    categoria: data.categoria || 'inventario',
    descripcion: data.descripcion || '',
    updatedAt: serverTimestamp(),
  };
  await updateDoc(doc(db, 'compras', id), payload);
}
